import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { ShortAmountPipe } from 'src/app/config/short-amount.pipe';
import { CommonService } from 'src/app/shared/common.service';
import { encodeFilterObject, refillQuotationFilter } from 'src/app/shared/utils/helpers';
import { IPagination } from 'src/app/shared/models/page.model';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { IQuotationFilter, IQuotationLocationFilter } from 'src/app/features/quotations/models/quotations.models';
import { DownloadCatalog } from 'src/app/features/catalogs/store/catalogs.actions';

import { IConversation, IConversationCustomer, IConversationCustomerCatalog, IConversationCustomerOrder, IConversationCustomerProduct, IConversationDetails, IConversationThreadContext } from '../../../models/conversation.models';
import { counterpartyDisagreesWithCompany } from '../../../utils/classification.util';
import * as smartInboxSelectors from '../../../store/smart-inbox.selectors';
import { ConversationService } from '../../../services/conversation.service';
import { Actions, ofType } from '@ngrx/effects';
import * as smartInboxActions from '../../../store/smart-inbox.actions';
import { SideBySideCorrectionPanelComponent, sideBySideResultMutatedOrder } from 'src/app/features/quotations/components/side-by-side-correction-panel/side-by-side-correction-panel.component';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { CustomerAssociationMenuComponent } from '../customer-association-menu/customer-association-menu.component';

export type ConversationDetailSidebarView = 'customer' | 'products' | 'catalogs';

/** Single listing item: either a product or a catalog (for one *ngFor with two templates). */
export type SidebarListingItem =
  | { kind: 'product'; data: IConversationCustomerProduct }
  | { kind: 'catalog'; data: IConversationCustomerCatalog };

// ─────────────────────────────────────────────────────────────────────────────
// ConversationDetailSidebarComponent
// ─────────────────────────────────────────────────────────────────────────────
// Sidebar for the conversation detail panel: customer info, orders, and products.
// Fetches active conversation and details from the store. Shows "No customer
// associated" when there is no customer; otherwise shows customer / orders /
// products sections. Products and catalogs use a single listings array with
// scroll-based pagination and separate row templates per kind.
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-conversation-detail-sidebar',
  standalone: true,
  host: { class: 'conversation-detail__sidebar' },
  templateUrl: './conversation-detail-sidebar.component.html',
  styleUrls: ['./conversation-detail-sidebar.component.scss'],
  imports: [
    CommonModule,
    UiModule,
    TranslateModule,
    SharedModule,
    ShortAmountPipe,
    MatMenuModule,
    CustomerAssociationMenuComponent,
  ],
})
export class ConversationDetailSidebarComponent implements OnInit, OnDestroy {

  // ── Public properties (from store) ────────────────────────────────────────

  activeConversationDetails: IConversationDetails | null = null;
  activeConversationThreadContext: IConversationThreadContext | null = null;

  // ── Public properties (local state) ────────────────────────────────────────

  sidebarView: ConversationDetailSidebarView = 'customer';

  /** Single array for products or catalogs; item shape depends on sidebarView. */
  listings: SidebarListingItem[] = [];
  page = 1;
  limit = 20;
  pagination: IPagination | null = null;
  loading = false;
  /** Guards the "Add contact" action against double-submit. */
  addingContact = false;
  searchInput = new FormControl<string>('');

  // ── Link-a-company (shown when the thread has no customer) ────────────────

  /** Whether the shared company picker menu is open (lazily renders its content). */
  customerMenuIsOpen = false;

  /**
   * Bound by template ref so it always resolves to this menu's trigger. Needed
   * to dismiss the overlay: the picker stops click propagation and its rows are
   * plain buttons rather than mat-menu-items, so Material never closes it.
   */
  @ViewChild('companyMenuTrigger') companyMenuTrigger?: MatMenuTrigger;

  // ── Private properties ────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();
  private readonly scrollThreshold = 80;
  private readonly toastOptions = { duration: 3000, verticalPosition: 'bottom' as const, horizontalPosition: 'right' as const };

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private conversationService: ConversationService,
    private commonService: CommonService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  // ── Link-a-company ────────────────────────────────────────────────────────

  get senderName(): string {
    const details = this.activeConversationDetails;
    return details?.thread_context?.sender?.name || details?.sender || '';
  }

  /**
   * Bare sender address. The API sometimes returns a full mailbox string
   * (`Display Name <someone@example.com>`), so unwrap the angle brackets rather
   * than showing the display name as if it were the address.
   */
  get senderEmail(): string {
    const details = this.activeConversationDetails;
    const raw = (details?.thread_context?.sender?.email || details?.sender_email || '').trim();
    const bracketed = raw.match(/<([^>]+)>/);
    return (bracketed ? bracketed[1] : raw).trim();
  }

  /**
   * Company-identifying part of the sender domain, with the public suffix
   * dropped: `notifications@github.com` → `github`.
   */
  get senderDomain(): string {
    const email = this.senderEmail;
    const at = email.lastIndexOf('@');
    if (at < 0) return '';

    const host = email.slice(at + 1);
    const lastDot = host.lastIndexOf('.');
    return lastDot > 0 ? host.slice(0, lastDot) : host;
  }

  /**
   * Sizes the shared picker to its trigger before opening, so the panel lands
   * exactly over the placeholder field ([overlapTrigger] handles the position).
   * The overlay renders outside this component, hence the CSS variable.
   */
  onOpenCompanyMenu(trigger: HTMLElement): void {
    document.documentElement.style.setProperty('--company-picker-width', `${trigger.offsetWidth}px`);
    this.customerMenuIsOpen = true;
  }

  /** Whether to warn that the linked company conflicts with the counterparty. */
  get showsCounterpartyWarning(): boolean {
    const details = this.activeConversationDetails;
    return !!details?.customer && counterpartyDisagreesWithCompany(details);
  }

  /** Links the thread to the company location picked in the shared menu. */
  onLinkCompany(customer: IConversationCustomer): void {
    const conversationId = this.activeConversationDetails?.id;
    if (conversationId == null) return;

    // Close the overlay itself, not just tear down its content via *ngIf —
    // otherwise an empty panel is left hanging over the sidebar.
    this.companyMenuTrigger?.closeMenu();
    this.customerMenuIsOpen = false;
    this.store.dispatch(smartInboxActions.UpdateConversations({
      conversationIds: [conversationId],
      payload: { customer_id: customer.id },
    }));
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.store.select(smartInboxSelectors.selectActiveConversationDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe((details) => {
        this.activeConversationDetails = details;
        this.activeConversationThreadContext = details?.thread_context ?? null;
      });

    this.searchInput.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((keyword) => this.loadListings(1, false, keyword?.trim() ?? ''));

    // Reset sidebar view and listings when conversation details are loaded
    this.actions$.pipe(
      ofType(smartInboxActions.LoadConversationDetailsSuccess),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.sidebarView = 'customer';
      this.listings = [];
      this.page = 1;
      this.customerMenuIsOpen = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Listings (products / catalogs) ──────────────────────────────────────────

  get hasMoreToLoad(): boolean {
    if (!this.pagination || this.loading) return false;
    return this.page < this.pagination.pages;
  }

  loadListings(pageNum: number, append: boolean, keyword: string): void {
    const conversationId = this.activeConversationDetails?.id;
    if (conversationId == null) return;
    if (this.loading) return;

    this.loading = true;
    const params = { page: pageNum, limit: this.limit, keyword: keyword || '' };

    const onSuccess = (response: APIResponse<IConversationCustomerProduct> | APIResponse<IConversationCustomerCatalog>, kind: 'product' | 'catalog') => {
      const data = response.data ?? [];
      const items: SidebarListingItem[] =
        kind === 'product'
          ? (data as IConversationCustomerProduct[]).map((d) => ({ kind: 'product' as const, data: d }))
          : (data as IConversationCustomerCatalog[]).map((d) => ({ kind: 'catalog' as const, data: d }));
      if (append) {
        this.listings = [...this.listings, ...items];
      } else {
        this.listings = items;
      }
      this.pagination = response.metadata;
      this.page = pageNum;
      this.loading = false;
    };

    if (this.sidebarView === 'products') {
      this.conversationService
        .getThreadCustomerProducts(conversationId, params)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => onSuccess(res, 'product'),
          error: () => { this.loading = false; },
        });
    } else if (this.sidebarView === 'catalogs') {
      this.conversationService
        .getThreadCustomerCatalogs(conversationId, params)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => onSuccess(res, 'catalog'),
          error: () => { this.loading = false; },
        });
    } else {
      this.loading = false;
    }
  }

  onListingsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el || !this.hasMoreToLoad) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - this.scrollThreshold) {
      this.loadListings(this.page + 1, true, this.searchInput.value?.trim() ?? '');
    }
  }

  trackByListingItem(_index: number, item: SidebarListingItem): number | string {
    return item.kind === 'product'
      ? (item.data.product_id ?? item.data.product_tmpl_id ?? _index)
      : (item.data.id ?? _index);
  }

  // ── Sidebar view & navigation ──────────────────────────────────────────────

  onSidebarViewChange(view: ConversationDetailSidebarView): void {
    this.sidebarView = view;
    if (view === 'products' || view === 'catalogs') {
      this.listings = [];
      this.page = 1;
      this.pagination = null;
      this.loadListings(1, false, this.searchInput.value?.trim() ?? '');
    }
  }

  viewCompany(): void {
    const id = this.activeConversationDetails?.customer?.id;
    if (id != null) {
      window.open(`${window.location.origin}/companies/${id}`, '_blank');
    }
  }

  /**
   * Add the sender's email to the matched company's email registry. On success,
   * reloads the conversation details so the sender's "known contact" badge updates.
   */
  addSenderToContacts(): void {
    const conversationId = this.activeConversationDetails?.id;
    if (conversationId == null || this.addingContact) return;

    this.addingContact = true;
    this.conversationService.addSenderToContacts(conversationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.addingContact = false;
          this.commonService.showToast(this.translate.instant('smart_inbox.toasts.contact_added'), 'INFO', this.toastOptions);
          this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId }));
        },
        error: () => {
          this.addingContact = false;
          this.commonService.showToast(this.translate.instant('smart_inbox.toasts.contact_add_failed'), 'WARNING', this.toastOptions);
        },
      });
  }

  // ── Order actions ────────────────────────────────────────────────────────

  openOrderCorrectionPanel(order: IConversationCustomerOrder): void {
    // window.open(`${window.location.origin}/quotations/${order.id}`, '_blank');
    // return;
    this.dialog.open(SideBySideCorrectionPanelComponent, {
      data: { quotationId: order.id, origin: 'smart-inbox' },
      panelClass: ['side-panel-slide-in', 'side-panel-full'],
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      // Refresh the conversation so this sidebar's order card — and the timeline —
      // pick up the change; the smart-inbox store is not what the panel updates.
      if (!sideBySideResultMutatedOrder(result)) return;
      const conversationId = this.activeConversationDetails?.id;
      if (conversationId != null) {
        this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId }));
      }
    });
  }

  viewAllOrders(): void {
    const activeConversationCustomer = this.activeConversationDetails?.customer;
    if (activeConversationCustomer?.id == null) return;
    const customer: IQuotationLocationFilter = {
      id: activeConversationCustomer.id,
      name: activeConversationCustomer.location,
    };
    const filter: IQuotationFilter = refillQuotationFilter();
    filter.organizeFilter.locations = [customer];
    const encoded = encodeFilterObject(filter);
    window.open(`${window.location.origin}/quotations?filter=${encoded}`, '_blank');
  }

  // ── Product actions ────────────────────────────────────────────────────────

  copyProduct(product: IConversationCustomerProduct): void {
    const lineBreak = '\r\n';
    const text = [
      `Product: ${product.name || '—'}`,
      `SKU: ${product.sku || '—'}`,
      `EAN: ${product.barcode || '—'}`,
      `MSRP: ${product.sales_price != null && product.sales_price > 0 ? product.sales_price : '—'}`,
      `Stock: ${product.available_quantity} units`,
    ].join(lineBreak);

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(
        () => this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copied_to_clipboard'), 'INFO', this.toastOptions),
        () => this.fallbackCopyToClipboard(text),
      );
    } else {
      this.fallbackCopyToClipboard(text);
    }
  }

  viewProductFull(product: IConversationCustomerProduct): void {
    window.open(`${window.location.origin}/inventory/${product.product_tmpl_id}`, '_blank');
  }

  // ── Catalog actions ────────────────────────────────────────────────────────

  viewCatalogFull(catalog: IConversationCustomerCatalog): void {
    window.open(`${window.location.origin}/catalogs/edit-catalog/${catalog.id}`, '_blank');
  }

  downloadCatalog(catalog: IConversationCustomerCatalog): void {
    this.store.dispatch(DownloadCatalog({ id: catalog.id }));
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  getCurrencySymbol(): string {
    const currencySymbol = this.activeConversationThreadContext?.company_currency?.symbol;
    return currencySymbol ?? '€';
  }

  /** Returns modifier class for stock pill: positive (green), negative (red), zero (neutral). */
  getStockPillClass(stock: number): string {
    if (stock > 0) return 'conversation-detail__sidebar-product-card-stock--positive';
    if (stock < 0) return 'conversation-detail__sidebar-product-card-stock--negative';
    return 'conversation-detail__sidebar-product-card-stock--zero';
  }

  getOrderStatusStyles(status: string): { [key: string]: string } {
    let textColor = '';
    let backgroundColor = '';
    const borderColor = '';

    switch (status) {
      case 'all':
        backgroundColor = '#949494';
        textColor = '#FFFFFF';
        break;
      case 'sent_to_erp':
      case 'done':
      case 'sale':
        backgroundColor = '#DCFFF1';
        textColor = '#174B35';
        break;
      case 'pending_inventory':
        backgroundColor = '#FFE489';
        textColor = '#785500';
        break;
      case 'cancel':
      case 'declined':
      case 'deleted':
        backgroundColor = '#FBDDD8';
        textColor = '#970000';
        break;
      case 'draft':
        backgroundColor = '#E2E2E2';
        textColor = '#595959';
        break;
      default:
        backgroundColor = '#E9F2FF';
        textColor = '#6449DB';
        break;
    }

    return {
      'background-color': backgroundColor,
      'color': textColor,
      'border-color': borderColor,
    };
  }

  // ── Private methods ──────────────────────────────────────────────────────

  /** Fallback method to copy text to clipboard if clipboard API is not available. */
  private fallbackCopyToClipboard(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const ok = document.execCommand('copy');
      if (ok) {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copied_to_clipboard'), 'INFO', this.toastOptions);
      } else {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copy_failed'), 'WARNING', this.toastOptions);
      }
    } catch {
      this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copy_failed'), 'WARNING', this.toastOptions);
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
